import React, { useEffect, useRef, useState } from 'react';
import { OrganizationProfile, useOrganization } from '@clerk/clerk-react';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrganizationType } from 'modules/Common/utils/Utility';
import DeclarantInfoForm from 'modules/Declarant/components/DeclarantInfoForm';
import ConsultantInfoForm from 'modules/Consultant/components/ConsultantInfoForm';
import OperatorInfoForm from 'modules/Operator/components/OperatorInfoForm';
import OrganizationPreferences from 'modules/App/components/OrganizationPreferences';
import OrgProfileRolesPanel from 'modules/PermissionsAdmin/components/OrgProfileRolesPanel';
import OrgProfilePlanPanel from 'modules/App/components/OrgProfilePlanPanel';
import WhatsNewPanel from 'modules/WhatsNew/components/WhatsNewPanel';
import { useReleaseNotes } from 'modules/WhatsNew/hooks/useReleaseNotes';
import { useTranslation } from 'react-i18next';

/**
 * Organization-management surface (Clerk <OrganizationProfile> — Members +
 * Settings + our custom Info/Preferences/Roles pages) shown in a lightweight
 * modal.
 *
 * These pages used to be registered on the global Header's
 * <OrganizationSwitcher>. The header-less NavSidebar layout dropped that
 * component, so the sidebar's "Manage organization" (a bare
 * clerk.openOrganizationProfile()) opened a profile with none of the custom
 * pages — the Members/Preferences regression. Re-mounting the same
 * <OrganizationProfile> here restores the full surface without bringing the
 * Header back. `routing="hash"` keeps it self-contained — it drives only
 * `window.location.hash` (which the app's path-based BrowserRouter ignores), so
 * the profile's internal tab navigation never touches app routes.
 */
export default function OrgProfileModal({ open, onClose }) {
  const { t, i18n } = useTranslation('app');
  const { organization } = useOrganization();
  const type = organization?.publicMetadata?.type;

  // D3: the What's New tab exists only once the backend actually serves notes.
  // Until then there is no tab at all — not an empty one, not an error one.
  //
  // Latched, and that is the point. Clerk's ClerkHostRenderer diffs
  // `customPages.length` on update and pushes the change into the mounted node,
  // so the tab appearing late is handled for us. The unhandled direction is the
  // tab DISAPPEARING while the user is standing on it: a refetch returning
  // `{releases: []}` would drop the <Page> while `routing="hash"` still points
  // at `#/whats-new`, stranding the profile on a route that no longer exists.
  // Once shown, the tab stays for the life of the modal.
  const { data: releases } = useReleaseNotes();
  const [hasReleases, setHasReleases] = useState(false);
  useEffect(() => {
    if (releases?.length) setHasReleases(true);
  }, [releases]);

  // Latest onClose via a ref so the effect below keys on `open` alone — a
  // parent passing an unstable callback must not churn the listener and the
  // body-overflow save/restore on every parent re-render (a re-captured
  // prevOverflow could even be another overlay's 'hidden', wedging the page
  // scroll-locked on close).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // True only when the click gesture STARTED on the backdrop — a drag that
  // begins inside the Clerk card (text selection in an input) and releases
  // over the backdrop dispatches its click on the backdrop and must not close.
  const mouseDownOnBackdropRef = useRef(false);

  // Escape-to-close + lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // An inner Clerk surface (dropdown, edit form) that handled Escape
      // marks the event — don't tear the whole modal down under it.
      if (e.key === 'Escape' && !e.defaultPrevented) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Clerk's routing="hash" leaves its last tab (#/organization-members…)
      // in the URL; strip the residue so it doesn't linger in the address bar
      // or re-open the profile on that tab next time.
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };
  }, [open]);

  if (!open) return null;

  // A verifier firm never sees Clerk's <OrganizationProfile>: its Members and Settings pages would
  // let a firm Admin invite any address and remove people without writing the firm's block list
  // (verifier-firm-foundation phase 1 §4, VF5–VF7). The firm's membership surface is its own
  // Team page (phase 2); here it gets Preferences only.
  const isVerifier = type === OrganizationType.Verifier;

  const infoForm =
    type === OrganizationType.Declarant ? (
      <DeclarantInfoForm />
    ) : type === OrganizationType.Consultant ? (
      <ConsultantInfoForm />
    ) : (
      <OperatorInfoForm />
    );
  const orgLabel =
    type === OrganizationType.Declarant
      ? t('orgType.declarant', 'Declarant')
      : type === OrganizationType.Consultant
        ? t('orgType.consultant', 'Consultant')
        : t('orgType.operator', 'Operator');

  return (
    // z-[1200]: above the app's overlay ladder (CgDialog 1050/1060, background-
    // jobs banner 1100, floating checklist 1048) so persistent chrome can't
    // paint or stay interactive over an open modal.
    <div
      className="fixed inset-0 z-[1200] flex items-start justify-center overflow-auto bg-slate-900/50 p-4 sm:p-8"
      onMouseDown={(e) => {
        mouseDownOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && mouseDownOnBackdropRef.current) onClose();
      }}
    >
      {/* The dialog role lives on the content, not the click-away backdrop.
          Stop propagation so clicks inside the profile don't close the modal. */}
      <div
        className="my-auto"
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.orgSwitcher.manageOrganization', 'Manage organization')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* key={i18n.language}: Clerk captures the custom-page children
            (Info/Preferences/Roles) at mount and renders them through its own
            portal — it does NOT re-read them when the app re-renders on
            `languageChanged`, so those tabs keep their first-mount translations
            while Clerk's native tabs (fed a fresh `localization` prop in _app)
            re-localize. Remounting the profile when the active language changes
            reproduces the same fresh mount that reopening the panel already
            does; `routing="hash"` restores the active tab from the URL hash
            across the remount, so the user stays where they were. */}
        {isVerifier ? (
          <div className="w-[min(640px,92vw)] rounded-xl bg-white p-6 shadow-xl">
            <h2 className="m-0 mb-4 text-lg font-semibold text-slate-900">
              {t('orgProfile.preferencesTab', 'Preferences')}
            </h2>
            <OrganizationPreferences />
          </div>
        ) : (
          <OrganizationProfile
            key={i18n.language}
            routing="hash"
          >
            <OrganizationProfile.Page
              url="org-info"
              label={t('orgProfile.infoTab', '{{orgLabel}} Info', { orgLabel })}
              labelIcon={<FontAwesomeIcon icon={faIcons.faUser} />}
            >
              {infoForm}
            </OrganizationProfile.Page>
            <OrganizationProfile.Page
              url="preferences"
              label={t('orgProfile.preferencesTab', 'Preferences')}
              labelIcon={<FontAwesomeIcon icon={faIcons.faSliders} />}
            >
              <OrganizationPreferences />
            </OrganizationProfile.Page>
            <OrganizationProfile.Page
              url="roles"
              label={t('orgProfile.rolesTab', 'Roles')}
              labelIcon={<FontAwesomeIcon icon={faIcons.faUserShield} />}
            >
              <OrgProfileRolesPanel />
            </OrganizationProfile.Page>
            {/* Consultants have neither an Operator nor a Declarant entity, so
              they have no plan to show — the tab is omitted rather than
              rendering an empty state. Clerk's useCustomPages skips falsy
              children (`if (child && !allowForAnyChildren)`), so the false
              branch neither warns nor throws. */}
            {type !== OrganizationType.Consultant && (
              <OrganizationProfile.Page
                url="plan"
                label={t('orgProfile.planTab', 'Plan')}
                labelIcon={<FontAwesomeIcon icon={faIcons.faCreditCard} />}
              >
                <OrgProfilePlanPanel />
              </OrganizationProfile.Page>
            )}
            {hasReleases ? (
              <OrganizationProfile.Page
                url="whats-new"
                label={t('whatsNew.tab', "What's new")}
                labelIcon={<FontAwesomeIcon icon={faIcons.faBullhorn} />}
              >
                <WhatsNewPanel />
              </OrganizationProfile.Page>
            ) : null}
          </OrganizationProfile>
        )}
      </div>
    </div>
  );
}
